import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  isProtectedPostMediaUrl,
  useProtectedMedia,
} from "../../hooks/useProtectedMedia";
import { getPostUrl } from "../../Utils/helpers";

interface ProtectedImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  url: string | undefined;
  fallbackSrc?: string;
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  as?: React.ElementType;
  fetchPriority?: "high" | "low" | "auto";
}

const mediaStateStyle: React.CSSProperties = {
  minHeight: "100%",
  width: "100%",
  background: "#000",
  color: "rgba(255,255,255,0.68)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.85rem",
};

const QuietMediaState = ({ label }: { label?: string }) => (
  <div style={mediaStateStyle}>{label}</div>
);

const MAX_PUBLIC_MEDIA_RETRIES = 2;
const MAX_BACKGROUND_RECOVERIES = 2;
const PUBLIC_MEDIA_RETRY_DELAYS_MS = [350, 1200];

const withMediaRetry = (src: string | undefined, requestVersion: number) => {
  if (!src || requestVersion === 0) return src;
  const separator = src.includes("?") ? "&" : "?";
  return `${src}${separator}fanxo_media_retry=${requestVersion}`;
};

const isPublicUrl = (url?: string) => {
  if (!url) return false;
  return (
    (url.startsWith("http") && !isProtectedPostMediaUrl(url)) ||
    url.startsWith("blob:") ||
    url.startsWith("data:") ||
    url.includes("processed_images") ||
    url.includes("/v1/uploads/") ||
    url.includes("/v1/media/profile-pictures/") ||
    url.includes("/v1/media/background-images/") ||
    url.includes("/v1/media/background-pictures/") ||
    url.includes("/v1/media/locked-preview/") ||
    url.includes("/v1/media/others/") ||
    url.startsWith("profile-pictures/") ||
    url.startsWith("background-images/") ||
    url.startsWith("background-pictures/") ||
    url.startsWith("others/")
  );
};

const ProtectedImage = ({
  url,
  fallbackSrc,
  loadingComponent,
  errorComponent,
  as: Component = "img",
  fetchPriority,
  ...imgProps
}: ProtectedImageProps): React.ReactElement | null => {
  const [didFail, setDidFail] = useState(false);
  const [publicRequestVersion, setPublicRequestVersion] = useState(0);
  const [publicRetryCount, setPublicRetryCount] = useState(0);
  const retryTimerRef = useRef<number | null>(null);
  const retryScheduledRef = useRef(false);
  const backgroundRecoveriesRef = useRef(0);
  const { objectUrl, loading, error } = useProtectedMedia(url);
  const publicUrl = isPublicUrl(url);
  const retryablePublicUrl = Boolean(
    publicUrl && url && !url.startsWith("blob:") && !url.startsWith("data:"),
  );
  const publicBaseSrc = publicUrl && url ? getPostUrl(url) : undefined;
  const immediatePublicSrc = withMediaRetry(
    publicBaseSrc,
    retryablePublicUrl ? publicRequestVersion : 0,
  );
  const resolvedSrc = didFail
    ? fallbackSrc
    : immediatePublicSrc ?? objectUrl ?? undefined;
  const priorityProps = fetchPriority
    ? ({ fetchPriority } as Record<string, string>)
    : {};

  const clearPublicTimers = useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    retryScheduledRef.current = false;
  }, []);

  const restartPublicLoad = useCallback(() => {
    clearPublicTimers();
    setDidFail(false);
    setPublicRetryCount(0);
    setPublicRequestVersion((version) => version + 1);
  }, [clearPublicTimers]);

  const queuePublicRetry = useCallback(() => {
    if (!retryablePublicUrl || didFail || retryScheduledRef.current) return;

    if (publicRetryCount >= MAX_PUBLIC_MEDIA_RETRIES) {
      setDidFail(true);
      return;
    }

    retryScheduledRef.current = true;
    const delay = PUBLIC_MEDIA_RETRY_DELAYS_MS[publicRetryCount] ?? 1200;
    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      retryScheduledRef.current = false;
      setPublicRetryCount((count) => count + 1);
      setPublicRequestVersion((version) => version + 1);
    }, delay);
  }, [didFail, publicRetryCount, retryablePublicUrl]);

  useEffect(() => {
    clearPublicTimers();
    setDidFail(false);
    setPublicRequestVersion(0);
    setPublicRetryCount(0);
    backgroundRecoveriesRef.current = 0;
  }, [clearPublicTimers, url]);


  useEffect(() => {
    if (
      !retryablePublicUrl ||
      !didFail ||
      backgroundRecoveriesRef.current >= MAX_BACKGROUND_RECOVERIES
    ) {
      return;
    }

    const recoveryTimer = window.setTimeout(() => {
      backgroundRecoveriesRef.current += 1;
      restartPublicLoad();
    }, 15000);
    return () => window.clearTimeout(recoveryTimer);
  }, [didFail, restartPublicLoad, retryablePublicUrl]);

  useEffect(() => {
    if (!retryablePublicUrl) return;

    const recoverWhenVisible = () => {
      if (document.visibilityState === "visible" && didFail) {
        backgroundRecoveriesRef.current = 0;
        restartPublicLoad();
      }
    };
    const recoverWhenOnline = () => {
      backgroundRecoveriesRef.current = 0;
      restartPublicLoad();
    };

    window.addEventListener("online", recoverWhenOnline);
    document.addEventListener("visibilitychange", recoverWhenVisible);
    return () => {
      window.removeEventListener("online", recoverWhenOnline);
      document.removeEventListener("visibilitychange", recoverWhenVisible);
    };
  }, [didFail, restartPublicLoad, retryablePublicUrl]);

  useEffect(() => clearPublicTimers, [clearPublicTimers]);

  const handleError: React.ReactEventHandler<HTMLImageElement> = (event) => {
    imgProps.onError?.(event);
    if (retryablePublicUrl && !didFail) {
      queuePublicRetry();
      return;
    }
    if (fallbackSrc && !didFail) {
      setDidFail(true);
    }
  };

  const handleLoad: React.ReactEventHandler<HTMLImageElement> = (event) => {
    clearPublicTimers();
    if (retryablePublicUrl && !didFail) {
      backgroundRecoveriesRef.current = 0;
    }
    imgProps.onLoad?.(event);
  };

  if (publicUrl) {
    return (
      <Component
        {...imgProps}
        {...priorityProps}
        loading={imgProps.loading ?? "lazy"}
        src={resolvedSrc}
        onLoad={handleLoad}
        onError={handleError}
      />
    );
  }

  if (!url) {
    return fallbackSrc ? (
      <Component {...imgProps} src={fallbackSrc} onError={imgProps.onError} />
    ) : (
      (errorComponent as React.ReactElement) ?? <QuietMediaState />
    );
  }

  if (loading) {
    return <>{loadingComponent ?? <QuietMediaState />}</>;
  }

  if (error) {
    return (
      (errorComponent as React.ReactElement) ??
      (fallbackSrc ? (
        <Component {...imgProps} src={fallbackSrc} onError={imgProps.onError} />
      ) : (
        <QuietMediaState label="Media unavailable" />
      ))
    );
  }

  return (
    <Component
      {...imgProps}
      {...priorityProps}
      loading={imgProps.loading ?? "lazy"}
      src={resolvedSrc ?? ""}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
};

export default ProtectedImage;

